import { TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Toast } from "@/components/ui/Toast";
import { useStudioApp } from "@/hooks/useStudioApps";
import {
  StudioShareError,
  usePublishAppShare,
  useShareForApp,
  useUnpublishAppShare,
} from "@/hooks/useStudioShares";

import { AppArtifactView } from "./AppArtifactView";

export interface AppArtifactContainerProps {
  /** Path-as-identity (spec R3): the app's directory under `studio/`. */
  appDir: string;
  /** Compact toolbar for narrow hosts (the chat artifact panel). */
  compact?: boolean;
}

const SHARE_ERROR_TOAST_MS = 3200;

/**
 * Fetch container resolving an `appDir` to a live `AppArtifactView` via the
 * real hooks (studio spec §4.4): `useStudioApp` for the files (fetch-on-mount
 * + manual refresh — mounted only while its host surface is open), the
 * and the reactive share query for the strip (publishing is open to every
 * plan — spec change log v1.0.1). Used by the chat `ArtifactPanel`
 * (identity-only `{kind: "app", appDir}` payload) and the world window's
 * open state.
 */
export function AppArtifactContainer({ appDir, compact = false }: AppArtifactContainerProps) {
  const { app, isLoading, error, refresh } = useStudioApp(appDir);
  const share = useShareForApp(appDir);
  const publish = usePublishAppShare();
  const unpublish = useUnpublishAppShare();

  const [shareError, setShareError] = useState<string | null>(null);
  const [isShareBusy, setIsShareBusy] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const surfaceShareError = useCallback((err: unknown) => {
    const message =
      err instanceof StudioShareError ? err.userMessage : "Something went wrong. Please try again.";
    setShareError(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setShareError(null), SHARE_ERROR_TOAST_MS);
  }, []);

  const onShare = useCallback(async () => {
    setIsShareBusy(true);
    try {
      await publish({ appDir });
    } catch (err) {
      surfaceShareError(err);
    } finally {
      setIsShareBusy(false);
    }
  }, [publish, appDir, surfaceShareError]);

  const onUnshare = useCallback(async () => {
    setIsShareBusy(true);
    try {
      await unpublish({ appDir });
    } catch (err) {
      surfaceShareError(err);
    } finally {
      setIsShareBusy(false);
    }
  }, [unpublish, appDir, surfaceShareError]);

  if (app === null) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
        {isLoading ? "Loading app…" : (error ?? "This app isn't available.")}
      </div>
    );
  }

  return (
    <>
      <AppArtifactView
        app={app}
        compact={compact}
        onRefresh={() => void refresh()}
        share={{
          url: share?.url ?? null,
          isBusy: isShareBusy,
          onShare: () => void onShare(),
          onUnshare: () => void onUnshare(),
        }}
      />
      <Toast
        isVisible={shareError !== null}
        message={shareError ?? ""}
        icon={<TriangleAlert className="h-4 w-4 text-home-terracotta" />}
      />
    </>
  );
}
